import React, { createContext, useContext, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { RootState } from "@/redux/store";
import { setIsValid } from "@/redux/slices/network/netWorkSlice";
import ValidateLoader from "../components/ValidateLoader";

// Define the steps
const steps = [
    { id: "1", title: "Define", description: "Description 1" },
    { id: "2", title: "Pallet Configuration", description: "Description 2" },
    { id: "3", title: "LPN No", description: "Description 3" },
    { id: "4", title: "Pre-Ship", description: "Description 4" },
    { id: "5", title: "Truck Layout", description: "Description 5" },
    { id: "6", title: "Post-Ship", description: "Description 6" },
    { id: "7", title: "Stack Test Layout", description: "Description 7" },
    { id: "8", title: "Post-Stack", description: "Description 8" },
    { id: "9", title: "Summary", description: "Description 9" },
    { id: "10", title: "Synopsis", description: "Description 10" },
    { id: "11", title: "Images", description: "Description 11" }
];

// Define the type for the context
interface StepperContextType {
    steps: typeof steps;
    stepper: {
        current: (typeof steps)[number];
        next: () => Promise<void>;
        previous: () => void;
        goTo: (index: number) => Promise<void>;
        when: (
            id: string,
            render: (step: {
                id: string;
                title: string;
                description: string;
            }) => JSX.Element
        ) => JSX.Element | null;
    };
    isValid: boolean;
    isLoading: boolean;
    validateConfig: () => Promise<boolean>;
}

// Provide a default value
const defaultStepperContext: StepperContextType = {
    steps,
    stepper: {
        current: steps[0], // Start at the first step by default
        next: async () => { },
        previous: () => { },
        goTo: async () => { },
        when: () => null,
    },
    isValid: false,
    isLoading: false,
    validateConfig: async () => false,
};

// Create the context with the default value
const StepperContext = createContext<StepperContextType>(defaultStepperContext);

// Provider component with state management
export const StepperProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const dispatch = useDispatch();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const { projectId } = useSelector((state: RootState) => state.define);
    const { isValid } = useSelector((state: RootState) => state.netWork);

    const validateConfig = async (): Promise<boolean> => {
        setIsLoading(true);
        try {
            if (location.pathname.includes('offline')) {
                // For offline mode, we'll assume validation passes
                dispatch(setIsValid(true));
                return true;
            } else {
                // In a real app, you would make an API call here
                // For now, we'll just simulate validation
                if (!isValid) {
                    // Simulate API call
                    await new Promise(resolve => setTimeout(resolve, 500));
                    const mockValidation = Math.random() > 0.3; // 70% chance of success
                    dispatch(setIsValid(mockValidation));
                    if (!mockValidation) {
                        console.error('Please fill all necessary fields of previous stage before moving forward');
                    }
                    return mockValidation;
                } else {
                    return true;
                }
            }
        } catch (error) {
            console.error('Error validating config:', error);
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const stepper = {
        current: steps[currentStepIndex],
        next: async () => {
            if (currentStepIndex < steps.length - 1) {
                setCurrentStepIndex((prevIndex) => prevIndex + 1);
            }
        },
        previous: () => {
            if (currentStepIndex > 0) {
                setCurrentStepIndex((prevIndex) => prevIndex - 1);
            }
        },
        goTo: async (index: number) => {
            if (index >= 0 && index < steps.length) {
                setCurrentStepIndex(index);
            }
        },
        when: (
            id: string,
            render: (step: {
                id: string;
                title: string;
                description: string;
            }) => JSX.Element
        ) => {
            const step = steps[currentStepIndex];
            return step.id === id ? render(step) : null;
        },
    };

    return (
        <StepperContext.Provider value={{ steps, stepper, isValid, isLoading, validateConfig }}>
            {isLoading && <ValidateLoader />}
            {children}
        </StepperContext.Provider>
    );
};

// Custom hook to use the stepper context
export const useStepperContext = () => {
    const context = useContext(StepperContext);
    if (!context) {
        throw new Error("useStepperContext must be used within a StepperProvider");
    }
    return context;
}; 